import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, Globe, Mail, Phone, MapPin, Users, Activity, TrendingUp, Edit2, Loader2, UserPlus, MoreHorizontal, Trash2 } from 'lucide-react';
import { fetchCompanyById, fetchUsers } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function CompanyDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isMasterTenant, tenant } = useTenant();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<any>(null);
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  
  // Use "Branch" for white-label tenants, "Company" for master tenant
  const entityName = isMasterTenant ? 'Company' : 'Branch';
  const entityNamePlural = isMasterTenant ? 'Companies' : 'Branches';

  useEffect(() => {
    async function loadCompany() {
      if (!id) return;
      setLoading(true);
      try {
        const [companyData, usersData] = await Promise.all([
          fetchCompanyById(id),
          fetchUsers({ company: id })
        ]);
        setCompany(companyData);
        setCompanyUsers(usersData);
      } catch (error) {
        console.error('Error loading company:', error);
        toast.error('Failed to load company details');
      } finally {
        setLoading(false);
      }
    }
    loadCompany();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading company...</p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Company Not Found</h1>
          <button
            onClick={() => navigate('/admin/companies')}
            className="text-primary hover:underline"
          >
            Back to Companies
          </button>
        </div>
      </div>
    );
  }

  

  const industryColors = {
    pharmaceutical: 'bg-blue-50 border-blue-200 text-blue-700',
    hospital: 'bg-green-50 border-green-200 text-green-700',
    research: 'bg-purple-50 border-purple-200 text-purple-700',
    medtech: 'bg-orange-50 border-orange-200 text-orange-700',
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Hero banner */}
        <div className="relative h-40 md:h-52 w-full overflow-hidden rounded-2xl mb-6">
          {company.banner_image_url ? (
            <img src={company.banner_image_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/85 to-primary/55" />
          )}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.25),transparent_60%)]" />
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/40 to-transparent" />
          <button
            onClick={() => navigate('/admin/companies')}
            className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-black/25 text-white backdrop-blur-sm hover:bg-black/40 transition-colors"
          >
            <ArrowLeft size={18} />
            Back to {entityNamePlural}
          </button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {company.logo ? (
                <img src={company.logo} alt={company.name} className="w-20 h-20 rounded-xl" />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                  <Building2 className="text-primary" size={32} />
                </div>
              )}
              <div>
                <h1 className="text-4xl font-bold text-foreground mb-2">{company.name}</h1>
                <div className="flex items-center gap-3">
                  {company.industry && (
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${industryColors[company.industry] || 'bg-muted border-border text-muted-foreground'}`}>
                      {company.industry.charAt(0).toUpperCase() + company.industry.slice(1)}
                    </span>
                  )}
                  {(() => {
                    const status = company.status || (company.is_active ? 'active' : 'inactive');
                    const isActive = status === 'active';
                    return (
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                        isActive ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate(`/admin/users/create?company=${company.id}`)}
                className="flex items-center gap-2"
              >
                <UserPlus size={16} />
                Add User
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/companies/${company.id}/edit`)}
                className="flex items-center gap-2"
              >
                <Edit2 size={16} />
                Edit {entityName}
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Info Cards */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview Card */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-bold text-foreground mb-4">Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <Users className="mx-auto mb-2 text-primary" size={24} />
                  <div className="text-2xl font-bold text-foreground">{company.total_users}</div>
                  <div className="text-xs text-muted-foreground">Total Users</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <Activity className="mx-auto mb-2 text-green-600" size={24} />
                  <div className="text-2xl font-bold text-green-600">{company.active_users}</div>
                  <div className="text-xs text-muted-foreground">Active Users</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <TrendingUp className="mx-auto mb-2 text-blue-600" size={24} />
                  <div className="text-2xl font-bold text-blue-600">{company.total_simulations}</div>
                  <div className="text-xs text-muted-foreground">Simulations</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <Activity className="mx-auto mb-2 text-orange-600" size={24} />
                  <div className="text-2xl font-bold text-orange-600">{company.avg_team_score}</div>
                  <div className="text-xs text-muted-foreground">Avg Score</div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                {company.website && (
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Globe size={18} />
                    <span>{company.website.replace(/^https?:\/\//, '')}</span>
                  </a>
                )}
                {company.contact_email && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Mail size={18} />
                    <span>{company.contact_email}</span>
                  </div>
                )}
                {company.contact_phone && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Phone size={18} />
                    <span>{company.contact_phone}</span>
                  </div>
                )}
                {company.address && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <MapPin size={18} />
                    <span>{company.address}, {company.city}, {company.state} {company.country}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Users List */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-foreground">{entityName} Users</h2>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => navigate(`/admin/users/create?company=${company.id}`)}
                  >
                    <UserPlus size={14} className="mr-1" />
                    Add User
                  </Button>
                  {companyUsers.length > 5 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/admin/users?company=${company.id}`)}
                    >
                      View All ({companyUsers.length})
                    </Button>
                  )}
                </div>
              </div>
              {companyUsers.length > 0 ? (
                <div className="space-y-3">
                  {companyUsers.slice(0, 5).map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                          <span className="text-sm font-bold text-primary">
                            {user.name?.split(' ').map((n: string) => n[0]).join('') || '?'}
                          </span>
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">{user.name || user.display_name || user.email}</div>
                          <div className="text-sm text-muted-foreground">{user.job_title || user.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">{user.simulations_completed || 0} sims</span>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                        >
                          <MoreHorizontal size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="mx-auto h-12 w-12 text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground mb-4">No users in this {entityName.toLowerCase()} yet</p>
                  <Button
                    onClick={() => navigate(`/admin/users/create?company=${company.id}`)}
                  >
                    <UserPlus size={16} className="mr-2" />
                    Add First User
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Performance */}
          <div className="space-y-6">
            {/* Team Performance */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-bold text-foreground mb-4">Team Performance</h2>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground">Average Score</span>
                    <span className="text-2xl font-bold text-primary">{company.avg_team_score}/100</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div 
                      className="bg-primary h-3 rounded-full transition-all duration-500"
                      style={{ width: `${company.avg_team_score}%` }}
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Total Simulations</span>
                    <span className="text-lg font-bold text-foreground">{company.total_simulations}</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Active Users</span>
                    <span className="text-lg font-bold text-foreground">{company.active_users}/{company.total_users}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Member Since</span>
                    <span className="text-sm font-medium text-foreground">{company.created_at}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-bold text-foreground mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate(`/admin/users/create?company=${company.id}`)}
                >
                  <UserPlus size={16} className="mr-2" />
                  Add User to {entityName}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate(`/admin/users?company=${company.id}`)}
                >
                  <Users size={16} className="mr-2" />
                  View All Users
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate(`/admin/companies/${company.id}/edit`)}
                >
                  <Edit2 size={16} className="mr-2" />
                  Edit {entityName} Details
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
